import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, Star } from "lucide-react";
import { CONTACT, telHref } from "@/config/contact";
import { useI18n } from "@/i18n/i18n";

export default function Hero() {
  const { tr } = useI18n();
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur px-4 py-2"
            >
              <span className="h-2 w-2 rounded-full bg-[color:var(--mc-cyan)] animate-pulse" />
              <Star className="h-3.5 w-3.5 text-[color:var(--mc-gold)] fill-[color:var(--mc-gold)]" />
              <span className="overline text-white/80">{tr.hero.badge}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight mt-7 leading-[1.02] text-white"
            >
              {tr.hero.title_pre}{" "}
              <em className="not-italic font-serif-display italic bg-gradient-to-r from-[color:var(--mc-cyan-2)] via-[color:var(--mc-cyan)] to-[color:var(--mc-primary-3)] bg-clip-text text-transparent">
                {tr.hero.title_em}
              </em>
              ,
              <br className="hidden sm:block" />
              {tr.hero.title_post}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.16 }}
              className="mt-7 text-lg text-white/75 max-w-xl leading-relaxed"
            >
              {tr.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <Link to="/contacto" className="btn-gold" data-testid="hero-cta-consulta">
                {tr.hero.cta_primary} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={telHref} className="btn-ghost" data-testid="hero-cta-phone">
                <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
              </a>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl"
            >
              {tr.hero.bullets.map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-white/75">
                  <CheckCircle2 className="h-4 w-4 text-[color:var(--mc-cyan)]" />
                  {t}
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-blue-950/40"
            >
              <img
                src="https://images.pexels.com/photos/7821671/pexels-photo-7821671.jpeg?auto=compress&cs=tinysrgb&w=1400"
                alt={tr.hero.hero_alt}
                className="w-full h-[520px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--mc-bg)] via-[color:var(--mc-bg)]/30 to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="absolute -bottom-6 -left-6 mc-card p-5 max-w-[240px] hidden sm:block"
            >
              <p className="overline text-[color:var(--mc-gold)]">{tr.hero.stat_label}</p>
              <p className="font-heading text-3xl font-semibold mt-2 text-white">{tr.hero.stat_value}</p>
              <p className="text-sm text-white/65 mt-1">{tr.hero.stat_caption}</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
